import React, { useMemo, useState } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Patient } from '../../types';
import { Users, UserPlus, Search, Calendar, Clock, CheckCircle, AlertCircle, Stethoscope, Baby, FileText, ArrowRight, ClipboardCheck, BarChart3 } from 'lucide-react';
import { ReceptionServiceCards, ReceptionTab } from './ReceptionServiceCards';
import { ReceptionServiceCharts } from './ReceptionServiceCharts';
import { PatientReports } from './PatientReports';
import { PatientReviewDueList } from './PatientReviewDueList';
import { ReceptionQueue } from './ReceptionQueue';
import { ActiveMaternityList } from './ActiveMaternityList';
interface Props {}
export const ReceptionDashboard: React.FC<Props> = () => {
  const {
    patients,
    addPatient,
    encounters,
    createEncounter,
    currentUser,
    getPatientEncounters,
    addMaternityFile,
    maternityFiles
  } = useData();
  const [activeTab, setActiveTab] = useState<ReceptionTab>('overview');
  const [showMaternityForm, setShowMaternityForm] = useState(false);
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    sex: 'Male' as 'Male' | 'Female',
    age: '',
    maritalStatus: 'Single' as 'Married' | 'Single',
    address: '',
    contact: '',
    occupation: '',
    nextOfKin: '',
    nokContact: '',
    nokRelationship: '',
    allergies: '',
    hivStatus: 'Unknown' as 'Positive' | 'Negative' | 'Unknown',
    isVitalsCompleted: false
  });
  // Maternity Form State
  const [maternityForm, setMaternityForm] = useState({
    firstName: '',
    surname: '',
    otherNames: '',
    age: '',
    religion: '',
    churchAttended: '',
    residentialAddress: '',
    locationLandmark: '',
    phone: '',
    nokName: '',
    nokPhone: '',
    nokRelationship: '',
    spouseFirstName: '',
    spouseSurname: '',
    spouseOtherNames: '',
    spouseOccupation: '',
    spouseContact: ''
  });
  const handleRegister = (e: React.FormEvent) => {
    e.preventDefault();
    const newPatient = addPatient({
      ...formData,
      age: parseInt(formData.age) || 0
    });
    // Auto create encounter and send to nursing
    createEncounter(newPatient.id);
    // Reset form and show success
    setFormData({
      firstName: '',
      lastName: '',
      sex: 'Male',
      age: '',
      maritalStatus: 'Single',
      address: '',
      contact: '',
      occupation: '',
      nextOfKin: '',
      nokContact: '',
      nokRelationship: '',
      allergies: '',
      hivStatus: 'Unknown'
    });
    alert(`Patient registered successfully! BMC Number: ${newPatient.bmcFileNumber}. Sent to Nursing.`);
  };
  const handleRegisterMaternity = (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;
    const now = new Date();
    const yy = String(now.getFullYear()).slice(-2);
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const prefix = `MAT-${yy}${mm}`;
    const monthCount = maternityFiles.filter(m => m.bmcFileNumber.startsWith(prefix)).length;
    const sequence = String(monthCount + 1).padStart(3, '0');
    const bmcFileNumber = `${prefix}${sequence}`;
    addMaternityFile({
      bmcFileNumber,
      createdAt: new Date().toISOString(),
      createdBy: currentUser.id,
      stage: 'NURSING',
      patient: {
        firstName: maternityForm.firstName,
        surname: maternityForm.surname,
        otherNames: maternityForm.otherNames,
        age: maternityForm.age,
        religion: maternityForm.religion,
        churchAttended: maternityForm.churchAttended,
        residentialAddress: maternityForm.residentialAddress,
        locationLandmark: maternityForm.locationLandmark,
        phone: maternityForm.phone,
        nextOfKin: {
          name: maternityForm.nokName,
          phone: maternityForm.nokPhone,
          relationship: maternityForm.nokRelationship
        }
      },
      spouse: {
        firstName: maternityForm.spouseFirstName,
        surname: maternityForm.spouseSurname,
        otherNames: maternityForm.spouseOtherNames,
        occupation: maternityForm.spouseOccupation,
        contact: maternityForm.spouseContact
      },
      admission: {
        date: '',
        time: ''
      },
      generalExam: {
        temp: '',
        pulse: '',
        respiration: '',
        bp: '',
        oedema: '',
        anemia: '',
        cardiovascularSystem: '',
        respiratorySystem: '',
        centralNervousSystem: '',
        breasts: '',
        varicosities: '',
        vaginalDischarge: '',
        otherAbnormalities: '',
        msUrine: {
          sg: '',
          albumen: '',
          acetone: '',
          sugar: ''
        },
        hb: '',
        abo: '',
        rh: ''
      },
      presentPregnancy: {
        lmp: '',
        quickening: '',
        edd: '',
        gestationAge: '',
        ancSummaryFirstBp: '',
        ancSummaryAtWeeks: '',
        syphilisTest: {
          date: '',
          result: '',
          treatment: ''
        },
        hpTest: {
          date: '',
          result: ''
        },
        pelvicAssessment: '',
        abnormalities: ''
      },
      labourObservations: [],
      delivery: {
        laceration: '',
        anterior: '',
        posterior: '',
        none: '',
        repaired: '',
        membranes: '',
        membranesDate: '',
        membranesTime: '',
        episiotomy: '',
        episiotomyRepaired: '',
        maternalARV: '',
        arvTypes: '',
        uterineMassage: '',
        motherOnHAART: '',
        cytotec: '',
        syntometrine: '',
        firstStageBeganDate: '',
        firstStageBeganTime: '',
        placentaDelivery: '',
        completeDilationDate: '',
        completeDilationTime: '',
        placentaDeliveredDate: '',
        placentaDeliveredTime: '',
        abnormalityAtDilation: '',
        abnormalityAtDilationDesc: '',
        postpartumHemorrhage: '',
        deliveryMethod: '',
        ebl: '',
        abnormalityAtDelivery: '',
        abnormalityAtDeliveryDesc: '',
        complications: '',
        deliverySummary: []
      },
      newbornEvaluation: {
        ega: '',
        alive: '',
        neonatalDeath: '',
        causeOfDeath: '',
        apgar1: '',
        apgar5: '',
        apgar10: '',
        generalExam: '',
        congenitalMalformation: '',
        congenitalMalformationDesc: '',
        infantARVs: '',
        infantFeeding: '',
        resuscitationMethods: '',
        crownHeelLength: '',
        headCircumference: '',
        skinJaundice: '',
        skinOther: '',
        fontanelles: '',
        eyes: '',
        eyesTreatment: '',
        mouth: '',
        neck: '',
        chest: '',
        abdomen: '',
        umbilicus: '',
        genitalia: '',
        urine: '',
        anus: '',
        meconium: '',
        upperLimbs: '',
        erbParalysis: '',
        lowerLimbs: '',
        ortolaniSign: '',
        back: '',
        reflexesGrasp: '',
        reflexesMoro: '',
        reflexesSucking: '',
        progressNotes: ''
      },
      postDeliveryObservations: [],
      puerperiumNotes: []
    });
    setMaternityForm({
      firstName: '',
      surname: '',
      otherNames: '',
      age: '',
      religion: '',
      churchAttended: '',
      residentialAddress: '',
      locationLandmark: '',
      phone: '',
      nokName: '',
      nokPhone: '',
      nokRelationship: '',
      spouseFirstName: '',
      spouseSurname: '',
      spouseOtherNames: '',
      spouseOccupation: '',
      spouseContact: ''
    });
    alert(`Maternity patient registered successfully. File Number: ${bmcFileNumber}. File forwarded to Nursing.`);
    setShowMaternityForm(false);
  };
  return <div className="space-y-6" data-id="element-6098" data-mp="24iofs1u55wdo">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between" data-id="element-6099" data-mp="29isy7xem116j">
        <h1 className="text-3xl font-bold tracking-tight text-gray-900" data-id="element-6100" data-mp="1ultjlplcc82k">
          Reception Dashboard
        </h1>
        <nav className="flex flex-wrap gap-2" data-id="element-6101" data-mp="1zkd55ow0vhue">
          <button onClick={() => setActiveTab('register')} className={`px-4 py-2 font-medium text-sm rounded-t-lg ${activeTab === 'register' ? 'bg-white text-blue-600 border-t-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-6102" data-mp="1zl52b459pnms">
            <UserPlus className="w-4 h-4 inline-block mr-2 mb-1 text-blue-600" strokeWidth={2.25} aria-hidden="true" data-id="element-6103" data-mp="2djmnau3sgm10" />
            Register New
          </button>
          <button onClick={() => setActiveTab('queue')} className={`px-4 py-2 font-medium text-sm rounded-t-lg ${activeTab === 'queue' ? 'bg-white text-blue-600 border-t-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-6104" data-mp="367s7nfqxk90b">
            <Users className="w-4 h-4 inline-block mr-2 mb-1 text-blue-600" strokeWidth={2.25} aria-hidden="true" data-id="element-6105" data-mp="umhuhfxchsex" />
            Queue
          </button>
          <button onClick={() => setActiveTab('reports')} className={`px-4 py-2 font-medium text-sm rounded-t-lg ${activeTab === 'reports' ? 'bg-white text-blue-600 border-t-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-6106" data-mp="14bjcfbngajvj">
            <FileText className="w-4 h-4 inline-block mr-2 mb-1 text-blue-600" strokeWidth={2.25} aria-hidden="true" data-id="element-6107" data-mp="pmwur3acpg15" />
            Patient Reports
          </button>
          <button onClick={() => setActiveTab('reviewDue')} className={`px-4 py-2 font-medium text-sm rounded-t-lg ${activeTab === 'reviewDue' ? 'bg-white text-blue-600 border-t-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-6108" data-mp="211isdr5xkko5">
            <ClipboardCheck className="w-4 h-4 inline-block mr-2 mb-1 text-blue-600" strokeWidth={2.25} aria-hidden="true" data-id="element-6109" data-mp="np2h185kk5pi" />
            Review Due
          </button>
          <button onClick={() => setActiveTab('maternity')} className={`px-4 py-2 font-medium text-sm rounded-t-lg ${activeTab === 'maternity' ? 'bg-white text-pink-600 border-t-2 border-pink-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-6110" data-mp="dzssg29xp565">
            <Baby className="w-4 h-4 inline-block mr-2 mb-1 text-blue-600" strokeWidth={2.25} aria-hidden="true" data-id="element-6111" data-mp="3w4vz3rfjtp8o" />
            Maternity Registration
          </button>
        </nav>
      </div>

      <ReceptionServiceCards activeTab={activeTab} onSelect={setActiveTab} data-id="element-6112" data-mp="1uchgsr93eirq" />

      {activeTab === 'overview' ? <ReceptionServiceCharts data-id="element-6113" data-mp="1pcd4zej4qc7u" /> : <button type="button" onClick={() => setActiveTab('overview')} className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-4 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-chrome-active" data-id="element-6114" data-mp="1wyqloe0edmrs">
          <BarChart3 className="h-4 w-4 text-chrome-active" aria-hidden="true" data-id="element-6115" data-mp="2boxus34iltft" />
          Back to dashboard charts
        </button>}

      {activeTab === 'register' && <Card title="Register New Patient" data-id="element-6116" data-mp="1iypll0ryijdd">
          <form onSubmit={handleRegister} className="space-y-6" data-id="element-6117" data-mp="1nxipq2lcsy0v">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-6118" data-mp="1nvz92avsckyk">
              <Input label="First Name" required value={formData.firstName} onChange={e => setFormData({
            ...formData,
            firstName: e.target.value
          })} data-id="element-6119" data-mp="1rzx5uexn1jog" />
              <Input label="Last Name" required value={formData.lastName} onChange={e => setFormData({
            ...formData,
            lastName: e.target.value
          })} data-id="element-6120" data-mp="147g4bxbphcqu" />
              <Input label="Age" type="number" required value={formData.age} onChange={e => setFormData({
            ...formData,
            age: e.target.value
          })} data-id="element-6121" data-mp="1891ps72b2zcn" />

              <Select label="Sex" options={[{
            value: 'Male',
            label: 'Male'
          }, {
            value: 'Female',
            label: 'Female'
          }]} required value={formData.sex} onChange={e => setFormData({
            ...formData,
            sex: e.target.value as any
          })} data-id="element-6122" data-mp="pk4srvfixsqq" />
              <Select label="Marital Status" options={[{
            value: 'Single',
            label: 'Single'
          }, {
            value: 'Married',
            label: 'Married'
          }]} required value={formData.maritalStatus} onChange={e => setFormData({
            ...formData,
            maritalStatus: e.target.value as any
          })} data-id="element-6123" data-mp="24h7oldr6gh3v" />
              <Input label="Contact Number" required value={formData.contact} onChange={e => setFormData({
            ...formData,
            contact: e.target.value
          })} data-id="element-6124" data-mp="1bttpb900b9gl" />

              <Input label="Home Address" className="md:col-span-2" required value={formData.address} onChange={e => setFormData({
            ...formData,
            address: e.target.value
          })} data-id="element-6125" data-mp="1ian18jvqe2hv" />
              <Input label="Occupation" value={formData.occupation} onChange={e => setFormData({
            ...formData,
            occupation: e.target.value
          })} data-id="element-6126" data-mp="20ztqkpr15scn" />
            </div>

            <div className="border-t border-gray-200 pt-4" data-id="element-6127" data-mp="kllexq7st3fs">
              <h4 className="text-md font-medium text-gray-900 mb-4" data-id="element-6128" data-mp="aiogiqrpo3fc">
                Emergency Contact
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-6129" data-mp="fgq2pkkfoiv1">
                <Input label="Next of Kin Name" required value={formData.nextOfKin} onChange={e => setFormData({
              ...formData,
              nextOfKin: e.target.value
            })} data-id="element-6130" data-mp="kgm216i6rm49" />
                <Input label="Relationship" required value={formData.nokRelationship} onChange={e => setFormData({
              ...formData,
              nokRelationship: e.target.value
            })} data-id="element-6131" data-mp="y6fsft9caeg3" />
                <Input label="Contact Number" required value={formData.nokContact} onChange={e => setFormData({
              ...formData,
              nokContact: e.target.value
            })} data-id="element-6132" data-mp="39x1luvnel8r4" />
              </div>
            </div>

            <div className="flex justify-end pt-4" data-id="element-6133" data-mp="36w89kwewksg2">
              <Button type="submit" size="lg" data-id="element-6134" data-mp="36udpfkbgh4d1">
                Register & Send to Nursing
              </Button>
            </div>
          </form>
        </Card>}

      {activeTab === 'queue' && <ReceptionQueue data-id="element-6135" data-mp="3fw3p1dfhr9lw" />}

      {activeTab === 'reports' && <PatientReports data-id="element-6136" data-mp="3ul9mavurqpr5" />}

      {activeTab === 'reviewDue' && <PatientReviewDueList data-id="element-6137" data-mp="3ukrfx6ee6v5i" />}

      {activeTab === 'maternity' && !showMaternityForm && <ActiveMaternityList onRegister={() => setShowMaternityForm(true)} data-id="element-6138" data-mp="3pmya4eh0435r" />}

      {activeTab === 'maternity' && showMaternityForm && <Card title="Register Maternity Patient" action={<Button variant="outline" size="sm" onClick={() => setShowMaternityForm(false)} data-id="element-6140" data-mp="3df1v08t2xtt">
              Back to active patients
            </Button>} data-id="element-6139" data-mp="3fhzo6g79v93">
          <form onSubmit={handleRegisterMaternity} className="space-y-6" data-id="element-6141" data-mp="d3di65vyhzak">
            <div data-id="element-6142" data-mp="i1x5oaq77j51">
              <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2" data-id="element-6143" data-mp="i1fbdjn3jezr">
                Patient Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-6144" data-mp="3c95xdxo0sjf">
                <Input label="First Name" required value={maternityForm.firstName} onChange={e => setMaternityForm({
              ...maternityForm,
              firstName: e.target.value
            })} data-id="element-6145" data-mp="840vpm8zyu8i" />
                <Input label="Surname" required value={maternityForm.surname} onChange={e => setMaternityForm({
              ...maternityForm,
              surname: e.target.value
            })} data-id="element-6146" data-mp="3glx12hsqvssp" />
                <Input label="Other Names" value={maternityForm.otherNames} onChange={e => setMaternityForm({
              ...maternityForm,
              otherNames: e.target.value
            })} data-id="element-6147" data-mp="3ryxos23jqrju" />
                <Input label="Age" type="number" required value={maternityForm.age} onChange={e => setMaternityForm({
              ...maternityForm,
              age: e.target.value
            })} data-id="element-6148" data-mp="3d8zv56mul9n9" />
                <Input label="Phone Number" required value={maternityForm.phone} onChange={e => setMaternityForm({
              ...maternityForm,
              phone: e.target.value
            })} data-id="element-6149" data-mp="2ucqj0oviat5w" />
                <Input label="Religion" value={maternityForm.religion} onChange={e => setMaternityForm({
              ...maternityForm,
              religion: e.target.value
            })} data-id="element-6150" data-mp="1ntp3kwh164ra" />
                <Input label="Church Attended" value={maternityForm.churchAttended} onChange={e => setMaternityForm({
              ...maternityForm,
              churchAttended: e.target.value
            })} data-id="element-6151" data-mp="103bk3ucihe8b" />
                <div className="md:col-span-2" data-id="element-6152" data-mp="ccfn8it77su7">
                  <Input label="Residential Address" required value={maternityForm.residentialAddress} onChange={e => setMaternityForm({
                ...maternityForm,
                residentialAddress: e.target.value
              })} data-id="element-6153" data-mp="3fxot6dba34lx" />
                </div>
                <div className="md:col-span-3" data-id="element-6154" data-mp="2xa37qno6oa1o">
                  <Input label="Location / Landmark" value={maternityForm.locationLandmark} onChange={e => setMaternityForm({
                ...maternityForm,
                locationLandmark: e.target.value
              })} data-id="element-6155" data-mp="319c0pqbeg3w6" />
                </div>
              </div>
            </div>

            <div data-id="element-6156" data-mp="2ihrpo27b8e6i">
              <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2" data-id="element-6157" data-mp="2ih9hc77di9i8">
                Next of Kin
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-6158" data-mp="261050bxbdo6a">
                <Input label="Name" required value={maternityForm.nokName} onChange={e => setMaternityForm({
              ...maternityForm,
              nokName: e.target.value
            })} data-id="element-6159" data-mp="25ynrcayl9zz1" />
                <Input label="Phone" required value={maternityForm.nokPhone} onChange={e => setMaternityForm({
              ...maternityForm,
              nokPhone: e.target.value
            })} data-id="element-6160" data-mp="1n97mjbfvflct" />
                <Input label="Relationship" required value={maternityForm.nokRelationship} onChange={e => setMaternityForm({
              ...maternityForm,
              nokRelationship: e.target.value
            })} data-id="element-6161" data-mp="19cakeduayhgm" />
              </div>
            </div>

            <div data-id="element-6162" data-mp="poo3i1mv7e9s">
              <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2" data-id="element-6163" data-mp="po64ix8tily6">
                Spouse Details (Optional)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-6164" data-mp="1df1zu4chfza6">
                <Input label="First Name" value={maternityForm.spouseFirstName} onChange={e => setMaternityForm({
              ...maternityForm,
              spouseFirstName: e.target.value
            })} data-id="element-6165" data-mp="1icbnpfk842vw" />
                <Input label="Surname" value={maternityForm.spouseSurname} onChange={e => setMaternityForm({
              ...maternityForm,
              spouseSurname: e.target.value
            })} data-id="element-6166" data-mp="1wbbaeho6e7pf" />
                <Input label="Other Names" value={maternityForm.spouseOtherNames} onChange={e => setMaternityForm({
              ...maternityForm,
              spouseOtherNames: e.target.value
            })} data-id="element-6167" data-mp="3umtnw9p4z7ci" />
                <Input label="Occupation" value={maternityForm.spouseOccupation} onChange={e => setMaternityForm({
              ...maternityForm,
              spouseOccupation: e.target.value
            })} data-id="element-6168" data-mp="2xydxkbe7ctyc" />
                <Input label="Contact" value={maternityForm.spouseContact} onChange={e => setMaternityForm({
              ...maternityForm,
              spouseContact: e.target.value
            })} data-id="element-6169" data-mp="m7joxagjmi8y" />
              </div>
            </div>

            <div className="flex justify-end pt-4" data-id="element-6170" data-mp="1wdxsej5epls8">
              <Button type="submit" size="lg" className="bg-pink-600 hover:bg-pink-700 text-white" data-id="element-6171" data-mp="1wfsdbxgohdwb">
                Register & Forward to Nursing
              </Button>
            </div>
          </form>
        </Card>}
    </div>;
};